import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

import { useCreateUserStandardMutation } from '../../standardsCatalogApi';

import type { UUID } from '@/shared/types/userStandard';

type NodeType = 'folder' | 'standard';

interface CreateNodeDialogProps {
  open: boolean;
  ownerId: UUID;
  parentId: UUID | null;
  defaultType?: NodeType;
  onClose: () => void;
}

export function CreateNodeDialog({
  open,
  ownerId,
  parentId,
  defaultType = 'standard',
  onClose,
}: CreateNodeDialogProps) {
  const [createNode, { isLoading }] = useCreateUserStandardMutation();
  // The root level holds only collections (folders) — a null parent always means "new collection",
  // never a root-level standard (see standards-library-v2-my-standards-tree.md, decision A2).
  const isCollection = parentId === null;
  const [type, setType] = useState<NodeType>(isCollection ? 'folder' : defaultType);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [statement, setStatement] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setType(isCollection ? 'folder' : defaultType);
      setName('');
      setCode('');
      setStatement('');
      setError(null);
    }
  }, [open, defaultType, isCollection]);

  const canSave = type === 'folder' ? name.trim().length > 0 : code.trim() || statement.trim();
  const folderNoun = isCollection ? 'collection' : 'folder';

  const handleCreate = async () => {
    setError(null);
    try {
      if (type === 'folder' || isCollection) {
        await createNode({ ownerId, parentId, nodeType: 'folder', title: name.trim() }).unwrap();
      } else {
        const text = statement.trim();
        await createNode({
          ownerId,
          parentId,
          nodeType: 'standard',
          title: text || code.trim(),
          code: code.trim() || null,
          description: text || null,
        }).unwrap();
      }
      onClose();
    } catch {
      setError(`Could not create the ${type}. Please try again.`);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create {type === 'folder' ? folderNoun : 'standard'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {!isCollection && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={type}
              onChange={(_event, next) => next && setType(next)}
            >
              <ToggleButton value="standard">Standard</ToggleButton>
              <ToggleButton value="folder">Folder</ToggleButton>
            </ToggleButtonGroup>
          )}

          {type === 'folder' ? (
            <TextField
              label={isCollection ? 'Collection name' : 'Name'}
              autoFocus
              fullWidth
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          ) : (
            <>
              <TextField
                label="Code"
                placeholder="e.g. 1.G.A.3"
                fullWidth
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
              <TextField
                label="Statement"
                fullWidth
                multiline
                minRows={3}
                value={statement}
                onChange={(event) => setStatement(event.target.value)}
              />
            </>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={!canSave || isLoading}>
          {isLoading ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreateNodeDialog;
